// Cable geometry + consumption engine for Standard Analytics.
// Builds a concentric layer model from a Standard's phases and computes material
// consumption, with two "hidden extra" analytics the naive plan ignores:
//   1. stranding take-up  — twisted conductors are longer than the rope (× factor)
//   2. wall thickness      — real (avg of min/max) wall vs the thin-side minimum
//
// Consumption ground truth mirrors the app: ringArea(mm²) × density(g/cm³) × len(m) / 1000 = kg.
import { blrKg } from './blr.js';

const PI = Math.PI;
// Strings only get the comma treatment: the wall inputs are typed by hand and
// `1,2` used to read as NaN → 0 → "no override", a silent no-op. Anything that
// is not a string keeps its old conversion, so an array like [1,2] still fails
// to a number instead of quietly becoming 1.2.
export const num = (v, d = 0) => {
  const n = typeof v === 'string' ? Number(v.trim().replace(',', '.')) : Number(v);
  return Number.isFinite(n) ? n : d;
};
export const discArea = dia => (PI * num(dia) * num(dia)) / 4; // mm²
const wireKgPerM = (dia, density = 8.96) => discArea(dia) * 1e-6 * density * 1000; // kg/m

// diameter of the circumscribing circle for n equal circles of diameter dc
// (cable rope packing). Lookup for common counts, formula fallback for n>6.
const PACK_RATIO = { 1: 1, 2: 2, 3: 2.1547, 4: 2.4142, 5: 2.7013, 6: 3.0 };
export function packDiameter(n, coreDiameter) {
  const dc = num(coreDiameter);
  if (n <= 1) return dc;
  const ratio = PACK_RATIO[n] || 1 + 1 / Math.sin(Math.PI / n);
  return dc * ratio;
}

// helical take-up: conductor metres per 1 m of rope
export function takeUpFactor(ropeDiameter, layLength) {
  const D = num(ropeDiameter);
  const pas = num(layLength);
  if (!(D > 0) || !(pas > 0)) return 1;
  const ratio = (PI * D) / pas;
  return Math.sqrt(1 + ratio * ratio);
}

// copper kg/m for one conductor (single solid or stranded bundle)
function conductorCopperKgPerM(wire) {
  if (!wire) return 0;
  const density = num(wire.density, 8.96);
  if (String(wire.type || '').toUpperCase() === 'SINGLE-WIRE') {
    return wireKgPerM(wire.diameter, density);
  }
  const diams = Array.isArray(wire.wireDiameter)
    ? wire.wireDiameter
    : wire.wireDiameter != null
      ? [wire.wireDiameter]
      : [];
  const counts = Array.isArray(wire.numberOfWires)
    ? wire.numberOfWires
    : wire.numberOfWires != null
      ? [wire.numberOfWires]
      : [];
  let kg = 0;
  diams.forEach((dd, i) => {
    kg += wireKgPerM(dd, density) * (num(counts[i]) || 1);
  });
  return kg;
}

// metal cross-section area (mm²) of one conductor — prefer stored, else from Ø
function conductorMetalArea(wire) {
  if (num(wire?.section) > 0) return num(wire.section);
  if (String(wire?.type || '').toUpperCase() === 'SINGLE-WIRE') return discArea(wire.diameter);
  const diams = Array.isArray(wire?.wireDiameter) ? wire.wireDiameter : [wire?.wireDiameter];
  const counts = Array.isArray(wire?.numberOfWires) ? wire.numberOfWires : [wire?.numberOfWires];
  let a = 0;
  diams.forEach((dd, i) => { a += discArea(dd) * (num(counts[i]) || 1); });
  return a;
}

const findPhase = (phases, name) =>
  (Array.isArray(phases) ? phases : []).find(p => String(p?.name).toLowerCase() === name);

// normalize conductor_isolation to a conductors[] array (handles flat FY/MYF shape)
function getConductors(std) {
  const ci = findPhase(std?.phases, 'conductor_isolation');
  if (!ci) return [];
  if (Array.isArray(ci.conductors) && ci.conductors.length) return ci.conductors;
  if (ci.wire) return [{ wire: ci.wire, isolation: ci.isolation }];
  return [];
}

export const WALL_SIDES = ['top', 'right', 'bottom', 'left'];
export const hasSideOverride = ov =>
  !!ov && WALL_SIDES.some(s => num(ov[s]) > 0);

// per-layer wall. min/max = spec tolerance; nominal = derived from stored Ø.
// Wall is modeled on 4 sides (top/right/bottom/left); each defaults to NOMINAL,
// so an un-edited layer reproduces the stored geometry. avg of the 4 drives area.
function wallOf(layerKey, phaseOrIso, innerD, overrides = {}) {
  const ov = overrides[layerKey] || {};
  const min = num(phaseOrIso?.minRadial);
  const max = num(phaseOrIso?.maxRadial);
  const storedOuter = num(phaseOrIso?.diameter);
  const nominal = storedOuter > 0 && innerD > 0 ? (storedOuter - innerD) / 2 : (min + max) / 2 || min || max;
  const sideVal = s => (num(ov[s]) > 0 ? num(ov[s]) : nominal);
  const sides = { top: sideVal('top'), right: sideVal('right'), bottom: sideVal('bottom'), left: sideVal('left') };
  const avg = (sides.top + sides.right + sides.bottom + sides.left) / 4;
  return { sides, nominal, min, max, avgSpec: (min + max) / 2 || nominal, avg };
}

// which wall thickness to use for the geometry/consumption, by scenario mode
function pickWall(wall, mode) {
  if (mode === 'nominal') return wall.nominal;
  if (mode === 'min') return wall.min > 0 ? wall.min : wall.nominal;
  if (mode === 'max') return wall.max > 0 ? wall.max : wall.nominal;
  if (mode === 'spec') return wall.avgSpec;
  return wall.avg; // 'custom' → mean of the 4 sides
}

/**
 * Build the full cable model + consumption.
 * @param {object} std   the Standard doc (with phases[])
 * @param {object} opts  { length, layLength, wallOverrides, wallMode }
 *   wallMode: 'nominal' (stored Ø, ground truth) | 'spec' (thin=min, thick=max) | 'custom' (use overrides)
 */
export function buildCableModel(std, opts = {}) {
  const length = num(opts.length, 0);
  const layLength = num(opts.layLength, 0);
  const wallMode = opts.wallMode || 'nominal';
  const overrides = opts.wallOverrides || {};
  // when true, the inner sheath fills the interstitial voids between conductors:
  // ringArea = disc(interior) − Σ disc(conductor) instead of the thin annulus.
  const interstitial = !!opts.interstitial;

  const phases = Array.isArray(std?.phases) ? std.phases : [];
  const nConductors = num(std?.numberOfConductors, 1) || 1;

  const conductors = getConductors(std);
  const ropePhase = findPhase(phases, 'rope');
  const intS = findPhase(phases, 'interior_sheat');
  const arm = findPhase(phases, 'armouring');
  const extS = findPhase(phases, 'exterior_sheat');

  const hasRope = !!ropePhase && nConductors > 1;

  // Delta-anchored geometry: stored diameters are the baseline (reproduce exactly
  // at no edits); the user's wall edits move a layer's outer Ø, and that shift
  // cascades to every downstream layer's inner Ø.
  const modeWall = (wall, key) => {
    if (hasSideOverride(overrides[key])) return wall.avg; // mean of the 4 edited sides
    return pickWall(wall, wallMode); // nominal/min/max/spec when no explicit override
  };

  // ── cores (copper + isolation ring) ──
  const cores = conductors.map((c, i) => {
    const wire = c.wire || {};
    const iso = c.isolation || {};
    const copperD = num(wire.diameter);
    const metalArea = conductorMetalArea(wire);
    const wall = wallOf(`iso_${i}`, iso, copperD, overrides);
    const ov = overrides[`iso_${i}`] || {};
    const dOverride = num(ov.diameter);
    const usedWall = modeWall(wall, `iso_${i}`);
    const outerD = dOverride > 0 ? dOverride : copperD + 2 * usedWall;
    const effWall = (outerD - copperD) / 2;
    const edited = dOverride > 0 || Math.abs(effWall - wall.nominal) > 1e-9;
    const ringArea = !edited && num(iso.fillingSection) > 0
      ? num(iso.fillingSection)
      : Math.max(0, discArea(outerD) - metalArea);
    return {
      index: i, color: iso.color || null, copperD, outerD, metalArea, ringArea,
      coreSection: discArea(outerD),
      isoMaterial: iso.material || 'PVC', isoType: iso.type || null, isoDensity: num(iso.density, 1.5),
      wall: { ...wall, avg: effWall }, usedWall: effWall
    };
  });

  const storedCoreOuter = cores.length
    ? Math.max(...conductors.map(c => num(c.isolation?.diameter) || num(c.wire?.diameter)))
    : 0;
  const coreOuterD = cores.length ? Math.max(...cores.map(c => c.outerD)) : 0;
  const sumCoreDiscs = cores.reduce((s, c) => s + discArea(c.outerD), 0); // Σ conductor areas
  const deltaCore = coreOuterD - storedCoreOuter;
  const packR = PACK_RATIO[nConductors] || (nConductors > 1 ? 1 + 1 / Math.sin(Math.PI / nConductors) : 1);
  const storedRopeD = hasRope ? (num(ropePhase.diameter) || packDiameter(nConductors, storedCoreOuter)) : storedCoreOuter;
  const packingBase = hasRope ? storedRopeD + packR * deltaCore : coreOuterD;
  // rope Ø: packing (+ conductor-edit cascade) + a small helical "lay bulge" that
  // grows as the pas tightens (each core juts out by coreØ·(1/cos α − 1)). The
  // auto bulge is displayed + feeds take-up, but does NOT disturb the sheath
  // consumption (kept at the standard); a MANUAL rope Ø override does cascade.
  const ropeManual = hasRope && num(overrides.rope?.diameter) > 0;
  let layAngle = 0;
  let ropeD = coreOuterD;
  if (hasRope) {
    const pitchD = Math.max(0, packingBase - coreOuterD);
    layAngle = layLength > 0 ? Math.atan((Math.PI * pitchD) / layLength) : 0;
    const bulge = coreOuterD * (1 / Math.cos(layAngle) - 1);
    ropeD = ropeManual ? num(overrides.rope.diameter) : packingBase + bulge;
  }
  const cascadeRopeD = ropeManual ? ropeD : packingBase; // bulge stays off the sheaths
  const takeUp = hasRope ? takeUpFactor(ropeD, layLength) : 1;

  // ── cable-level layers, delta-anchored to stored diameters ──
  const cableLayers = [];
  let storedPrev = storedRopeD;         // stored outer Ø of the previous layer
  let shift = hasRope ? cascadeRopeD - storedRopeD : deltaCore; // accumulated outward shift

  const addSheath = (phase, key) => {
    if (!phase) return;
    const storedInner = storedPrev;
    const storedOuter = num(phase.diameter);
    const nominalWall = storedOuter > 0 && storedInner > 0 ? (storedOuter - storedInner) / 2 : 0;
    const wall = wallOf(key, phase, storedInner, overrides);
    wall.nominal = nominalWall; // anchor to stored jump
    const dOverride = num(overrides[key]?.diameter);
    const usedWall = modeWall(wall, key);
    const innerD = storedInner + shift;
    const outerD = dOverride > 0 ? dOverride : innerD + 2 * usedWall;
    const effWall = (outerD - innerD) / 2;
    const untouched = dOverride <= 0 && Math.abs(usedWall - nominalWall) < 1e-9 && Math.abs(shift) < 1e-9;
    let ringArea = untouched && num(phase.fillingSection) > 0
      ? num(phase.fillingSection)
      : Math.max(0, discArea(outerD) - discArea(innerD));
    // interstitial correction: the sheath applied DIRECTLY over the rope (bedding)
    // fills the voids between the conductors. That is whichever sheath sits on the
    // rope — interior_sheat if present, else exterior_sheat (e.g. MYYM: rope→outer).
    if (interstitial && hasRope && sumCoreDiscs > 0 && Math.abs(storedInner - storedRopeD) < 1e-6) {
      ringArea = Math.max(0, discArea(outerD) - sumCoreDiscs);
    }
    cableLayers.push({
      key, kind: 'sheath', name: phase.name, innerD, outerD, ringArea,
      section: discArea(outerD),
      material: phase.material || 'PVC', type: phase.type || null, density: num(phase.density, 1.5),
      wall: { ...wall, nominal: nominalWall, avg: effWall }, usedWall: effWall,
      // The baseline this layer is anchored to, and how far an upstream edit
      // pushed its inner Ø away from it. Everything downstream keeps its wall
      // and moves outward, which is right for an extruder — but it means the
      // layer silently stops reaching the diameter the standard declares, and
      // nothing on screen said so. See realWallToStandard().
      storedInner, storedOuter, inboundShift: shift
    });
    storedPrev = storedOuter || outerD;
    shift = outerD - (storedOuter || outerD);
  };

  addSheath(intS, 'interior_sheat');

  if (arm) {
    const storedInner = storedPrev;
    const storedOuter = num(arm.diameter);
    const nominalThickness = num(arm.thickness);
    const ov = overrides.armouring || {};
    const thickness = num(ov.thin) > 0 ? num(ov.thin) : nominalThickness;
    const nominalBuild = storedOuter > 0 ? (storedOuter - storedInner) / 2 : 2 * nominalThickness;
    // two tapes: radial build = 2×thickness; preserve stored jump when unedited
    const build = Math.abs(thickness - nominalThickness) < 1e-9 ? nominalBuild : 2 * thickness;
    const innerD = storedInner + shift;
    const outerD = innerD + 2 * build;
    cableLayers.push({
      key: 'armouring', kind: 'armour', name: 'armouring', innerD, outerD,
      section: discArea(outerD), material: 'BLR',
      wide: num(arm.wide), thickness, density: num(arm.density, 7.85),
      storedInner, storedOuter, inboundShift: shift
    });
    storedPrev = storedOuter || outerD;
    shift = outerD - (storedOuter || outerD);
  }

  addSheath(extS, 'exterior_sheat');

  const outerDiameter = cableLayers.length ? cableLayers[cableLayers.length - 1].outerD : coreOuterD;

  // ── consumption ──────────────────────────────────────────────────────────
  const lines = [];
  const push = (group, label, kgPerM, meta = {}) =>
    lines.push({ group, label, kgPerM, kg: kgPerM * length, ...meta });

  // copper + isolation per core, × take-up (they travel the twisted path)
  let copperKgPerM = 0;
  let isoKgPerM = 0;
  const copperByD = {};
  const isoByColor = {};
  for (const core of cores) {
    const wire = conductors[core.index].wire;
    const cuPerM = conductorCopperKgPerM(wire) * takeUp;
    copperKgPerM += cuPerM;
    const cuKey = `Ø${num(core.copperD).toFixed(2)}`;
    copperByD[cuKey] = (copperByD[cuKey] || 0) + cuPerM;

    const isoPerM = (core.ringArea * core.isoDensity / 1000) * takeUp;
    isoKgPerM += isoPerM;
    const iKey = `${core.isoType || core.isoMaterial}${core.color ? ` (${core.color})` : ''}`;
    isoByColor[iKey] = (isoByColor[iKey] || 0) + isoPerM;
  }
  Object.entries(copperByD).forEach(([k, v]) =>
    push('COPPER', `Copper ${k}`, v, { takeUp })
  );
  Object.entries(isoByColor).forEach(([k, v]) =>
    push('PVC', `${k} · insulation`, v, { takeUp })
  );

  // sheaths + armour (straight along the rope, no take-up)
  let pvcKgPerM = isoKgPerM;
  let blrKgPerM = 0;
  for (const L of cableLayers) {
    if (L.kind === 'sheath') {
      const kgPerM = (L.ringArea * L.density) / 1000;
      pvcKgPerM += kgPerM;
      const layerLabel = L.name === 'interior_sheat' ? 'inner sheath' : 'outer sheath';
      push('PVC', `${L.type || L.material} · ${layerLabel}`, kgPerM);
    } else if (L.kind === 'armour') {
      const kgPerM = length > 0 ? blrKg(L.wide, L.thickness, L.density, length) / length : 0;
      blrKgPerM += kgPerM;
      push('BLR', `BLR ${L.wide} x ${L.thickness}`, kgPerM);
    }
  }

  return {
    name: std.name,
    numberOfConductors: nConductors,
    section: std.section,
    greenYellowSection: num(std?.greenYellowSection) || null,
    hasMixedConductorSections: !!std?.hasMixedConductorSections,
    color: std.color,
    hasRope,
    takeUp,
    layLength,
    ropeDiameter: ropeD,
    ropePackingD: packingBase,
    layAngleDeg: (layAngle * 180) / Math.PI,
    coreOuterD,
    outerDiameter,
    cores,
    cableLayers,
    length,
    lines,
    totals: {
      copperKg: copperKgPerM * length,
      pvcKg: pvcKgPerM * length,
      blrKg: blrKgPerM * length,
      totalKg: (copperKgPerM + pvcKgPerM + blrKgPerM) * length
    },
    perM: { copperKgPerM, pvcKgPerM, blrKgPerM }
  };
}

// Convenience: consumption at the three wall scenarios (for the "extra" analytics).
export function wallScenarios(std, { length, layLength } = {}) {
  const mk = mode => buildCableModel(std, { length, layLength, wallMode: mode });
  const nominal = mk('nominal');
  const spec = mk('spec'); // thin=min, thick=max → avg wall
  return {
    nominal,
    spec,
    takeUpExtraCopperKg: 0 // filled by caller comparing takeUp on/off if needed
  };
}

/**
 * The radial this layer would need to still reach the diameter the standard declares.
 *
 * The geometry is delta-anchored: an edit upstream moves this layer's inner Ø
 * and the layer keeps its wall, so its OUTER Ø moves with it. That is what an
 * extruder does — the head puts on the wall it is set to, whatever arrives.
 *
 * But the standard declares an outer diameter, and once the inner has moved the
 * layer no longer reaches it. The number below is the wall it WOULD have to run
 * to: smaller when the edit pushed the inner outward (the layer is being
 * squeezed), larger when it pulled it in. Reported rather than applied —
 * nothing here decides which of the two the operator meant.
 *
 * @returns null when this layer is not anchored or nothing upstream moved.
 */
export function realWallToStandard(layer) {
  const storedOuter = num(layer?.storedOuter);
  const innerD = num(layer?.innerD);
  const shift = num(layer?.inboundShift);
  if (!(storedOuter > 0) || !(innerD > 0)) return null;
  if (Math.abs(shift) < 1e-6) return null;

  const nominal = num(layer?.wall?.nominal);
  const real = (storedOuter - innerD) / 2;
  return {
    real,
    nominal,
    shift,
    // A negative wall means the inner is already wider than the standard's
    // outer: the layer cannot be built at all at this diameter.
    impossible: real <= 0,
    delta: real - nominal
  };
}
